import { useState, useRef, useEffect } from 'react';
import { handleCloudinaryUpload } from 'actions/utils';
import Button from 'components/atoms/Button';
import { getVideoThumbnail, isVideo } from 'utils/discover';
import PlayVideoButton from 'components/molecules/PlayVideoButton';
import Typography from 'components/atoms/Typography';
import FileProcessingIndicator from './FileProcessingIndicator';
import FileUploadingIndicator from './FileUploadingIndicator';

enum UploadStatus {
  FileUploading,
  FileProcessing,
  NotUploading,
}

export interface CloudinaryImageUploadProps {
  label?: string;
  required?: boolean;
  existingImg?: string;
  placeholder?: string;
  preview?: boolean;
  imageWidth?: number;
  handleImgSelect: (imgUrl: string) => void;
  className?: string;
  fileFilter?: string;
  disabled?: boolean;
  helperText?: string;
}

function CloudinaryImageUploader({
  existingImg,
  placeholder,
  preview,
  handleImgSelect,
  imageWidth,
  className,
  fileFilter,
  label,
  required,
  disabled,
  helperText,
}: CloudinaryImageUploadProps) {
  const fileSelect = useRef(null);
  const [image, setImage] = useState(existingImg || null);
  const [progress, setProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(UploadStatus.NotUploading);

  useEffect(() => {
    if (progress > 0 && progress < 100) {
      setUploadStatus(UploadStatus.FileUploading);
    } else if (progress === 100) {
      setUploadStatus(UploadStatus.FileProcessing);
    } else {
      setUploadStatus(UploadStatus.NotUploading);
    }
  }, [progress]);

  const handleCloudUrl = (url) => {
    setImage(url);
    handleImgSelect(url);
  };

  const handleUpload = (file) => {
    handleCloudinaryUpload(file, handleCloudUrl, setProgress);
  };

  const imageUploadPrompt = () => {
    if (fileSelect.current) {
      fileSelect.current.click();
    }
  };

  const handleRemove = (event) => {
    event.preventDefault();
    handleImgSelect('');
    setImage('');
    setProgress(0);
  };

  const hasVideoThumbnail = isVideo(image || '');

  const renderUploadStatus = () => {
    switch (uploadStatus) {
      case UploadStatus.FileUploading: {
        return <FileUploadingIndicator progress={progress} />;
      }
      case UploadStatus.FileProcessing: {
        return <FileProcessingIndicator />;
      }
      default: {
        return undefined;
      }
    }
  };

  return (
    <div className={className}>
      {label && (
        <div className="pb-2 pl-1">
          <Typography variant="label">
            {label}
            {required && <span className="text-red text-xs">*</span>}
          </Typography>
        </div>
      )}
      {helperText ? <p className="text-[11px] mb-5">{helperText}</p> : null}
      {image && preview ? (
        // Consider maybe using the react cloudinary component with preset transforms
        <div className="flex items-top justify-between">
          <div className="mb-4" style={{ maxWidth: imageWidth }}>
            {hasVideoThumbnail && (
              <div className="relative">
                <img
                  className="object-cover w-full rounded-sm"
                  src={getVideoThumbnail(image)}
                  alt="Kit Banner Video"
                  style={{ maxHeight: '400px' }}
                />
                {hasVideoThumbnail && <PlayVideoButton imageUrl={image} />}
              </div>
            )}
            {!hasVideoThumbnail && (
              <img
                className="object-contain rounded-sm"
                src={image.replace('upload/', `upload/w_${imageWidth}/`)}
                alt=""
              />
            )}
          </div>
          <div>
            <Button
              dataDurableId="removeImageButton"
              type="destruction"
              text="Remove"
              onClick={(e) => handleRemove(e)}
              disabled={disabled}
            />
          </div>
        </div>
      ) : (
        <figure>
          {uploadStatus === UploadStatus.NotUploading ? (
            <>
              {placeholder && !image && (
                <img className="object-contain rounded-sm" width={imageWidth} src={placeholder} alt="" />
              )}
              <Button
                dataDurableId="imageUploadButton"
                text="Browse"
                noBubble
                type="primary"
                className="my-4"
                onClick={imageUploadPrompt}
                disabled={disabled}
              />
            </>
          ) : (
            <div className="mt-2 mb-6">{renderUploadStatus()}</div>
          )}
        </figure>
      )}

      <input
        data-durable-id="cloudinaryImageUploadLinkId"
        data-intercom-target="cloudinaryImageUploadLinkId"
        ref={fileSelect}
        multiple={false}
        type="file"
        accept={fileFilter || 'image/*,.pdf'}
        style={{ display: 'none' }}
        onChange={(e) => handleUpload(e.target.files[0])}
        disabled={disabled}
      />
    </div>
  );
}

export default CloudinaryImageUploader;
